import { FC, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ActivityType } from '@/api/models/activity'
import {
  useFindActivityByIdAndTree,
  useFindActivityPartByPathIdAndTree,
} from '@/components/ActivityViewers/hooks/useFindActivityById'
import { useNavigateToActivityPartByAxis } from '@/hooks/useActivityParts/useNavigateToActivityPartByAxis'
import { useCourseNavigate } from '@/hooks/useCourseNavigate'
import { useLazyFetchActivityWorkflowAggregateFullTreeQuery } from '@/store/api/objectiveWorkflowApi'
import { useAppDispatch } from '@/store/hooks'
import { fetchCurrentFromObjectiveAsync } from '@/store/slices/aggregateSlice'
import { FrontActivityPart } from '@/store/slices/aggregateSlice/types/FrontActivityPart.type'
import { FrontActivityWithAggregate } from '@/store/slices/aggregateSlice/types/FrontActivityWithAggregate.type'
import { hydrateMetaAndSort } from '@/store/slices/aggregateSlice/utils/hydrateMetaAndSort'
import { addBlockingOperation, removeBlockingOperation } from '@/store/slices/loadingSlice'
import { log } from '@/utils/log'
import { captureException } from '@/utils/sentryUtils'

/**
 * Redirects to course details or content by activityId or without it
 * /learn/courses/redirect/<objective_id>
 * /learn/courses/redirect/<objective_id>?activityId=<activity_id>
 * /learn/courses/redirect/<objective_id>?activityPathId=<activityPathId> (Better)
 */

type Props = {
  onError: VoidFunction
}

export const CourseRedirectByActivityId: FC<Props> = (props) => {
  const { onError } = props

  const { objectiveId: objectiveIdRouteParam } = useParams()
  const dispatch = useAppDispatch()
  const { navigateToDetails, navigateToContent } = useCourseNavigate()
  const findActivityByIdAndTree = useFindActivityByIdAndTree()
  const findActivityPartByPathIdAndTree = useFindActivityPartByPathIdAndTree()
  const { navigateToActivityPartByAxis } = useNavigateToActivityPartByAxis()

  const [searchParams] = useSearchParams()
  const objectiveIdSearchParam = searchParams.get('objectiveId')
  const activityId = searchParams.get('activityId')
  const activityPathId = searchParams.get('activityPathId')
  const objectiveId = objectiveIdRouteParam || objectiveIdSearchParam

  const [fetchActivityWorkflowAggregateFullTreeQuery] = useLazyFetchActivityWorkflowAggregateFullTreeQuery()

  useEffect(() => {
    dispatch(addBlockingOperation())
    return () => {
      dispatch(removeBlockingOperation())
    }
  }, [dispatch])

  useEffect(() => {
    const getAggregate = async () => {
      try {
        return await dispatch(fetchCurrentFromObjectiveAsync(objectiveId as string)).unwrap()
      } catch {
        onError()
      }
    }

    const getTree = async (lastObjectiveWorkflowId: string) => {
      try {
        return fetchActivityWorkflowAggregateFullTreeQuery({
          objectiveWorkflowId: lastObjectiveWorkflowId,
        }).unwrap()
      } catch {
        onError()
      }
    }

    const init = async () => {
      try {
        const aggregate = await getAggregate()

        if (!aggregate) {
          return
        }

        const isComposite = aggregate?.objective?.activity?.type === ActivityType.composite
        const hasLastObjectiveWorkflow = !!aggregate.lastObjectiveWorkflow

        if (isComposite) {
          if (!hasLastObjectiveWorkflow) {
            log('Redirect to course details', { objectiveId })
            navigateToDetails(objectiveId!, true)
            return
          }

          const tree = (await getTree(aggregate.lastObjectiveWorkflow!.id)) as FrontActivityWithAggregate | null

          if (activityId) {
            const activity = findActivityByIdAndTree(
              activityId,
              hydrateMetaAndSort(structuredClone(tree)!),
            ) as FrontActivityPart<FrontActivityWithAggregate>

            if (activity) {
              log('Navigate to activity', { activityId, activity })
              navigateToActivityPartByAxis(activity?.axis!, { replace: true })

              return
            }

            log('Activity not found, redirect course details', { activityId })
            navigateToDetails(objectiveId!, true)

            return
          }

          if (activityPathId) {
            try {
              const activity = findActivityPartByPathIdAndTree(
                activityPathId,
                hydrateMetaAndSort(structuredClone(tree)!),
              ) as FrontActivityPart<FrontActivityWithAggregate>

              if (activity) {
                log('Navigate to activity', { activityPathId, activity })
                navigateToActivityPartByAxis(activity?.axis!, { replace: true })

                return
              }
            } catch (e) {
              console.log(e)

              log('Activity not found, redirect course details', { activityPathId })
              navigateToDetails(objectiveId!, true)
            }

            return
          }

          log('Redirect to course content, there is no activityId for composite', { objectiveId })
          navigateToContent(objectiveId!, false, true)

          return
        }

        if (!hasLastObjectiveWorkflow) {
          log('Redirect to course details', { objectiveId })
          navigateToDetails(objectiveId!, true)
          return
        }

        log('Redirect to course content', { objectiveId })
        navigateToContent(objectiveId!)
      } catch (error) {
        console.log('error', error)
        captureException(error)
        onError()
      } finally {
        dispatch(removeBlockingOperation())
      }
    }

    init()
  }, [
    activityId,
    activityPathId,
    dispatch,
    fetchActivityWorkflowAggregateFullTreeQuery,
    findActivityByIdAndTree,
    findActivityPartByPathIdAndTree,
    navigateToActivityPartByAxis,
    navigateToContent,
    navigateToDetails,
    objectiveId,
    onError,
  ])

  return null
}
